import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from '../components/common/loader';
import { fetchProducts } from '../store/slices/productSlice';

const Dashboard = () => {
  const { user } = useSelector(state => state.auth);
  const { products, pagination, loading } = useSelector(state => state.products);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('overview');
  
  useEffect(() => {
    // Fetch recent products when the dashboard loads
    dispatch(fetchProducts({ page: 1, limit: 4, sortBy: 'createdAt', sortOrder: 'desc' }));
  }, [dispatch]);
  
  // Format date helper
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">My Account</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center space-x-4 p-2 mb-6">
            <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-bold text-xl">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div>
              <p className="font-medium">{user?.name || 'User'}</p>
              <p className="text-sm text-gray-500">{user?.email || 'No email'}</p>
            </div>
          </div>
          
          <nav>
            <button 
              className={`w-full text-left py-2 px-4 rounded mb-1 ${
                activeTab === 'overview' 
                  ? 'bg-blue-100 text-blue-600 font-medium' 
                  : 'hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            
            <button 
              className={`w-full text-left py-2 px-4 rounded mb-1 ${
                activeTab === 'orders' 
                  ? 'bg-blue-100 text-blue-600 font-medium' 
                  : 'hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('orders')}
            >
              My Orders
            </button>
            
            <button 
              className={`w-full text-left py-2 px-4 rounded mb-1 ${
                activeTab === 'profile' 
                  ? 'bg-blue-100 text-blue-600 font-medium' 
                  : 'hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('profile')}
            >
              Profile Settings
            </button>
          </nav>
        </div>
        
        {/* Main Content */}
        <div className="md:col-span-3">
          {loading.products ? (
            <div className="bg-white rounded-lg shadow-md p-6 flex justify-center items-center min-h-[400px]">
              <Loader size="lg" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold mb-6">Account Overview</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-medium mb-2">Account Details</h3>
                      <p className="text-gray-700">{user?.name || 'Not available'}</p>
                      <p className="text-gray-700">{user?.email || 'Not available'}</p>
                      <p className="text-gray-700">{user?.phone || 'Not available'}</p>
                      <Link to="/profile" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
                        Edit Profile
                      </Link>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-medium mb-2">Order Summary</h3>
                      <p className="text-gray-700">Total Orders: 0</p>
                      <p className="text-gray-700">Pending Orders: 0</p>
                      <p className="text-gray-700">Completed Orders: 0</p>
                      <Link to="/orders" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
                        View Order History
                      </Link>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-medium">Recently Viewed Products</h3>
                      <Link to="/products" className="text-blue-600 hover:underline text-sm">
                        Browse Products
                      </Link>
                    </div>
                    
                    {products && products.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {products.map(product => (
                          <div key={product.productId} className="border rounded-lg overflow-hidden flex">
                            <div className="w-1/3 h-24">
                              <img 
                                src={product.imageUrl} 
                                alt={product.name} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="w-2/3 p-3">
                              <Link 
                                to={`/product/${product.productId}`}
                                className="font-medium text-sm hover:text-blue-600 line-clamp-2"
                              >
                                {product.name}
                              </Link>
                              <p className="text-sm font-bold mt-1">
                                ₹{product.price}
                                {product.mrp > product.price && (
                                  <span className="text-xs text-gray-500 line-through ml-1">
                                    ₹{product.mrp}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-4 text-center rounded-lg">
                        <p className="text-gray-600">No recently viewed products.</p>
                        <Link to="/products" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
                          Start Browsing
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold mb-6">My Orders</h2>
                  
                  <div className="bg-gray-50 p-8 text-center rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    <p className="text-gray-600 mb-4">You haven't placed any orders yet.</p>
                    <Link 
                      to="/products" 
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition inline-block"
                    >
                      Start Shopping
                    </Link>
                  </div>
                </div>
              )}
              
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold mb-6">Profile Settings</h2>
                  
                  <form>
                    <div className="grid grid-cols-1 gap-6 mb-6">
                      <div>
                        <label className="block text-gray-700 mb-2">Full Name</label>
                        <input
                          type="text"
                          defaultValue={user?.name || ''}
                          className="w-full border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 mb-2">Email Address</label>
                        <input
                          type="email"
                          defaultValue={user?.email || ''}
                          className="w-full border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 mb-2">Phone Number</label>
                        <input
                          type="tel"
                          defaultValue={user?.phone || ''}
                          className="w-full border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Phone number cannot be changed
                        </p>
                      </div>
                    </div>
                    
                    <div className="border-t pt-6">
                      <button 
                        type="submit" 
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;